import { Logger } from './logger';
const console = new Logger('GlyphData');
const GLYPH_DATA_URL = '/data/glyph-data.json.gz';
/**
 * Loads the build-time Glyph Data JSON once and keeps a JavaScript search
 * index for Add Glyphs and Glyph.glyphData lookups.
 */
export class GlyphDataIndex {
    constructor() {
        this.records = [];
        this.byCodepoint = new Map();
        this.byName = new Map();
        this.readyPromise = null;
    }
    isReady() {
        return this.records.length > 0;
    }
    async ensureReady() {
        if (this.isReady()) {
            return;
        }
        if (!this.readyPromise) {
            this.readyPromise = this.load().catch((error) => {
                this.readyPromise = null;
                throw error;
            });
        }
        return this.readyPromise;
    }
    getGlyphDataForUnicode(codepoints) {
        if (!codepoints || codepoints.length !== 1) {
            return undefined;
        }
        return this.byCodepoint.get(codepoints[0]);
    }
    getGlyphDataForName(name) {
        return this.byName.get(name);
    }
    search(query, limit = 150) {
        const normalized = query.trim().toLowerCase();
        if (!normalized) {
            return this.records.slice(0, limit);
        }
        const hex = normalized.replace(/^u\+|^0x/, '');
        const queryWords = normalized.split(/\s+/).filter(Boolean);
        const suffixSegment = normalized.startsWith('-')
            ? normalized
            : undefined;
        const results = [];
        for (const record of this.records) {
            const codepoint = record.codepoint.toString(16).toLowerCase();
            const name = record.glyph_name.toLowerCase();
            const unicodeName = record.name.toLowerCase();
            let score = 0;
            if (name === normalized || codepoint === hex) {
                score = 4;
            }
            else if (suffixSegment &&
                (name.endsWith(suffixSegment) ||
                    name.includes(`${suffixSegment}.`))) {
                score = 3;
            }
            else if (name.startsWith(normalized) ||
                codepoint.startsWith(hex)) {
                score = 3;
            }
            else if (queryWords.every((queryWord) => unicodeName
                .split(/\s+/)
                .some((word) => word.startsWith(queryWord)))) {
                score = 2;
            }
            else if (name.includes(normalized) ||
                unicodeName.includes(normalized) ||
                record.character === query) {
                score = 1;
            }
            if (score) {
                results.push({ record, score });
            }
        }
        return results
            .sort((left, right) => right.score - left.score ||
            left.record.glyph_name.localeCompare(right.record.glyph_name) ||
            left.record.codepoint - right.record.codepoint)
            .slice(0, limit)
            .map(({ record }) => record);
    }
    /** Test helper: replace the index with an in-memory catalog. */
    loadRecordsForTests(records) {
        this.applyRecords(records);
        this.readyPromise = Promise.resolve();
    }
    /** Test helper: clear the index so ensureReady() reloads. */
    resetForTests() {
        this.records = [];
        this.byCodepoint = new Map();
        this.byName = new Map();
        this.readyPromise = null;
    }
    async load() {
        const response = await fetch(GLYPH_DATA_URL);
        if (!response.ok) {
            throw new Error(`Unable to load Glyph Data catalog (${response.status}).`);
        }
        const records = JSON.parse(await decompressGzipResponse(response));
        this.applyRecords(records);
        window.dispatchEvent(new Event('counterpunch:glyph-data-ready'));
        console.log(`Loaded ${this.records.length} Glyph Data records.`);
    }
    applyRecords(records) {
        this.records = records.map((record) => ({
            ...record,
            general_category: record.general_category || record.category || '',
            character: characterForCodepoint(record.codepoint)
        }));
        this.byCodepoint = new Map(this.records.map((record) => [record.codepoint, record]));
        this.byName = new Map(this.records.map((record) => [record.glyph_name, record]));
    }
}
function characterForCodepoint(codepoint) {
    return codepoint >= 0x20 && codepoint !== 0x7f && codepoint <= 0x10ffff
        ? String.fromCodePoint(codepoint)
        : '';
}
async function decompressGzipResponse(response) {
    if (typeof DecompressionStream === 'undefined') {
        throw new Error('Gzip DecompressionStream is not available.');
    }
    if (!response.body) {
        throw new Error('Glyph Data response has no body.');
    }
    const stream = response.body.pipeThrough(new DecompressionStream('gzip'));
    return new Response(stream).text();
}
export const glyphDataIndex = new GlyphDataIndex();
globalThis.glyphDataIndex =
    glyphDataIndex;
// Static catalog — safe to warm during boot (no Pyodide). Skip in Jest
// where `fetch` is unset unless a test mocks it.
if (typeof fetch === 'function') {
    void glyphDataIndex
        .ensureReady()
        .catch((error) => console.warn('Glyph Data will be retried when opened.', error));
}
